import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../config/api';

interface Client {
  relationship_id: number;
  status: string;
  started_at: string;
  client_id: number;
  full_name: string;
  email: string;
  client_since: string;
}

interface CoachClientListProps {
  onClientSelect?: (clientId: number, clientName: string) => void;
  selectedClientId?: number | null;
}

export const CoachClientList: React.FC<CoachClientListProps> = ({ 
  onClientSelect, 
  selectedClientId 
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch coach's clients
  const { data: clientsData, isLoading, error } = useQuery({
    queryKey: ['coach-clients'],
    queryFn: async () => {
      try {
        return await apiClient.getCoachClients();
      } catch (error) {
        console.error('Error fetching clients:', error);
        throw error;
      }
    },
    retry: 1
  });

  const clients: Client[] = clientsData?.clients || [];
  
  // Filter clients based on search term
  const filteredClients = clients.filter(client =>
    client.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status: string) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    
    switch (status) {
      case 'active':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'inactive':
        return `${baseClasses} bg-gray-100 text-gray-800`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center space-x-4">
                  <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="text-center py-8">
            <div className="text-red-600 text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Clients</h3>
            <p className="text-gray-600">
              Unable to load your client list. Please try again.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Your Clients</h3>
        </div>
        <div className="card-body">
          <div className="text-center py-8">
            <div className="text-6xl mb-4">👥</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Clients Yet</h3>
            <p className="text-gray-600 mb-4">
              You haven't been assigned any clients yet.
            </p>
            <p className="text-sm text-gray-500">
              Clients will appear here once they are assigned to your coaching practice.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Your Clients</h3>
            <p className="text-sm text-gray-500 mt-1">
              {clients.length} client{clients.length !== 1 ? 's' : ''} total
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="mt-4 sm:mt-0 sm:ml-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card-body p-0">
        <div className="divide-y divide-gray-200">
          {filteredClients.map((client) => (
            <div
              key={client.relationship_id}
              className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                selectedClientId === client.client_id ? 'bg-blue-50 border-r-4 border-blue-500' : ''
              }`}
              onClick={() => onClientSelect?.(client.client_id, client.full_name)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                      <span className="text-sm font-medium text-white">
                        {client.full_name
                          .split(' ')
                          .map(n => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Client Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {client.full_name}
                      </p>
                      <span className={getStatusBadge(client.status)}>
                        {client.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">{client.email}</p>
                    <p className="text-xs text-gray-500">
                      Started coaching: {formatDate(client.started_at)}
                    </p>
                  </div>
                </div>
                
                {/* Quick Actions */}
                <div className="flex items-center space-x-2">
                  {selectedClientId === client.client_id && (
                    <div className="text-blue-600">
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Summary Footer */}
      <div className="card-footer bg-gray-50">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            <span className="font-medium">{filteredClients.length}</span> of{' '}
            <span className="font-medium">{clients.length}</span> clients
            {searchTerm && (
              <span className="ml-2">
                (filtered by "{searchTerm}")
              </span>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
              <span>Active: {clients.filter(c => c.status === 'active').length}</span>
            </div>
            {clients.some(c => c.status !== 'active') && (
              <div className="flex items-center">
                <div className="w-2 h-2 bg-gray-500 rounded-full mr-1"></div>
                <span>Other: {clients.filter(c => c.status !== 'active').length}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};